import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, X } from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'

import { useAuth } from '@/contexts/AuthContext'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { useOnlineBookingInbox } from '@/hooks/useOnlineBookingInbox'
import {
  appNavLabel,
  groupAppNavigation,
  isAppNavActive,
  NAV_GROUP_LABEL_KEYS,
  NAV_GROUP_ORDER,
  getVisibleAppNavigation,
} from '@/lib/appNavigation'
import { getNavIcon } from '@/lib/navIcons'
import { cn } from '@/lib/utils'

/** @typedef {import('@/lib/appNavigation').NavGroupId} NavGroupId */

/** @type {Record<NavGroupId, boolean>} */
const DEFAULT_GROUP_EXPANDED = {
  main: true,
  admin: false,
  masters: false,
  workspace: false,
}

/**
 * @param {NavGroupId} groupId
 * @param {string} pathname
 * @param {import('@/lib/appNavigation').AppNavEntry[]} entries
 */
function shouldExpandGroupForRoute(groupId, pathname, entries) {
  if (groupId === 'main') return true
  return entries.some((entry) => isAppNavActive(entry, pathname))
}

/**
 * @param {string} pathname
 * @param {Record<NavGroupId, import('@/lib/appNavigation').AppNavEntry[]>} grouped
 * @param {NavGroupId[]} visibleGroups
 * @returns {Record<NavGroupId, boolean>}
 */
function buildGroupExpandedState(pathname, grouped, visibleGroups) {
  /** @type {Record<NavGroupId, boolean>} */
  const next = { ...DEFAULT_GROUP_EXPANDED }

  visibleGroups.forEach((groupId) => {
    if (shouldExpandGroupForRoute(groupId, pathname, grouped[groupId])) {
      next[groupId] = true
    }
  })

  return next
}

/**
 * @param {{ count: number, collapsed: boolean }} props
 */
function OnlineBadge({ count, collapsed }) {
  if (!count || count <= 0) return null

  const label = count > 99 ? '99+' : String(count)

  return (
    <span
      className={cn(
        'flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold leading-none text-white',
        collapsed ? 'absolute -right-0.5 -top-0.5' : 'ml-auto',
      )}
    >
      {label}
    </span>
  )
}

/**
 * @param {{
 *   collapsed: boolean,
 *   onToggleCollapsed: () => void,
 *   mobileOpen: boolean,
 *   onMobileClose: () => void,
 *   isRtl?: boolean,
 * }} props
 */
export function AppSidebar({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onMobileClose,
  isRtl = false,
}) {
  const { user } = useAuth()
  const { t } = useUiLocale()
  const { unreadCount } = useOnlineBookingInbox()
  const location = useLocation()

  const grouped = useMemo(() => {
    const visible = getVisibleAppNavigation(user)
    return groupAppNavigation(visible)
  }, [user])

  const visibleGroups = NAV_GROUP_ORDER.filter((groupId) => grouped[groupId].length > 0)

  const [expandedGroups, setExpandedGroups] = useState(() =>
    buildGroupExpandedState(location.pathname, grouped, visibleGroups),
  )

  useEffect(() => {
    setExpandedGroups((current) => {
      const next = { ...current }
      let changed = false

      visibleGroups.forEach((groupId) => {
        if (shouldExpandGroupForRoute(groupId, location.pathname, grouped[groupId]) && !next[groupId]) {
          next[groupId] = true
          changed = true
        }
      })

      return changed ? next : current
    })
  }, [location.pathname, grouped, visibleGroups])

  const toggleGroup = (groupId) => {
    setExpandedGroups((current) => ({
      ...current,
      [groupId]: !current[groupId],
    }))
  }

  const CollapseIcon = isRtl ? PanelRightClose : PanelLeftClose
  const ExpandIcon = isRtl ? PanelRightOpen : PanelLeftOpen

  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border/50 px-3 py-3 lg:hidden">
        <p className="text-sm font-semibold tracking-tight">Menu</p>
        <button
          type="button"
          onClick={onMobileClose}
          className="flex h-8 w-8 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted/80 hover:text-foreground"
          aria-label="Close menu"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 pb-3 pt-2" aria-label="Main">
        {visibleGroups.map((groupId) => {
          const isGroupExpanded = collapsed || expandedGroups[groupId]
          const groupLabel = t(NAV_GROUP_LABEL_KEYS[groupId])
          const canToggleGroup = !collapsed && groupId !== 'main'
          const isMainGroup = groupId === 'main'

          return (
            <div key={groupId} className={cn('mb-2 last:mb-0', collapsed && 'mb-2')}>
              {isMainGroup && collapsed ? (
                <div className="mb-1 hidden justify-center lg:flex">
                  <button
                    type="button"
                    onClick={onToggleCollapsed}
                    className="flex h-8 w-8 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                    aria-label="Expand sidebar"
                  >
                    <ExpandIcon className="h-4 w-4" strokeWidth={2} aria-hidden />
                  </button>
                </div>
              ) : null}
              {!collapsed ? (
                isMainGroup ? (
                  <div className="mb-1.5 flex items-center gap-1 px-2">
                    <p className="min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      {groupLabel}
                    </p>
                    <button
                      type="button"
                      onClick={onToggleCollapsed}
                      className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground lg:flex"
                      aria-label="Collapse sidebar"
                    >
                      <CollapseIcon className="h-4 w-4" strokeWidth={2} aria-hidden />
                    </button>
                  </div>
                ) : canToggleGroup ? (
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupId)}
                    className="mb-1 flex w-full items-center gap-1 rounded-lg px-2 py-1 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                    aria-expanded={isGroupExpanded}
                  >
                    <ChevronDown
                      className={cn(
                        'h-3 w-3 shrink-0 transition-transform duration-200',
                        !isGroupExpanded && '-rotate-90',
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 truncate">{groupLabel}</span>
                  </button>
                ) : (
                  <p className="mb-1.5 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    {groupLabel}
                  </p>
                )
              ) : null}
              {isGroupExpanded ? (
                <ul className="space-y-0.5">
                  {grouped[groupId].map((entry) => {
                    const Icon = getNavIcon(entry.icon)
                    const showBadge = entry.id === 'online-bookings' && unreadCount > 0

                    return (
                      <li key={entry.id}>
                        <NavLink
                          to={entry.path}
                          end={entry.path === '/'}
                          title={collapsed ? appNavLabel(entry, t) : undefined}
                          onClick={onMobileClose}
                          className={({ isActive }) =>
                            cn(
                              'group relative flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-[13px] font-medium transition-colors',
                              collapsed && 'justify-center px-2',
                              isActive
                                ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
                                : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground',
                            )
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <Icon
                                className={cn(
                                  'h-[18px] w-[18px] shrink-0',
                                  isActive
                                    ? 'text-primary-foreground'
                                    : 'text-muted-foreground group-hover:text-foreground',
                                )}
                                strokeWidth={2.25}
                                aria-hidden
                              />
                              {!collapsed ? (
                                <span className="min-w-0 truncate">{appNavLabel(entry, t)}</span>
                              ) : null}
                              {showBadge ? (
                                <OnlineBadge count={unreadCount} collapsed={collapsed} />
                              ) : null}
                            </>
                          )}
                        </NavLink>
                      </li>
                    )
                  })}
                </ul>
              ) : null}
            </div>
          )
        })}
      </nav>
    </div>
  )

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] lg:hidden"
          aria-label="Close menu backdrop"
          onClick={onMobileClose}
        />
      ) : null}

      <aside
        className={cn(
          'app-sidebar flex shrink-0 flex-col border-border/60 bg-card/95 backdrop-blur-xl',
          collapsed ? 'app-sidebar--collapsed' : 'app-sidebar--expanded',
          isRtl ? 'border-l' : 'border-r',
          // Mobile: off-canvas drawer (fixed, full viewport height)
          'max-lg:fixed max-lg:inset-y-0 max-lg:z-50 max-lg:shadow-xl',
          isRtl ? 'max-lg:right-0' : 'max-lg:left-0',
          mobileOpen
            ? 'max-lg:translate-x-0'
            : isRtl
              ? 'max-lg:translate-x-full'
              : 'max-lg:-translate-x-full',
          // Desktop: sits in flex row directly under top bar — no sticky/top offset
          'lg:relative lg:h-full lg:translate-x-0 lg:shadow-none',
          'transition-transform duration-200 ease-out',
        )}
      >
        {sidebarContent}
      </aside>
    </>
  )
}
